import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useWallet } from "@solana/wallet-adapter-react"
import { useWalletModal } from "@solana/wallet-adapter-react-ui"
import { Button } from "./ui/button"
import { Wallet, LogOut, Loader2 } from "lucide-react"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { useWalletBalance } from "@/hooks/use-wallet-balance"
import { NetworkBadge, NetworkIndicator } from "./NetworkBadge"
import { showSuccessToast } from "@/lib/toast-helpers"

export function WalletDropdown({ isCollapsed = false }: { isCollapsed?: boolean } = {}) {
    const { t } = useTranslation()
    const { publicKey, disconnect, connecting } = useWallet()
    const { setVisible } = useWalletModal()
    const { balance, loadingBalance } = useWalletBalance()

    const prevKeyRef = useRef<string | null>(null)
    const [justConnected, setJustConnected] = useState(false)

    useEffect(() => {
        const key = publicKey?.toBase58() ?? null
        if (key && prevKeyRef.current !== key) {
            if (prevKeyRef.current === null) {
                showSuccessToast(t("wallet.connected"), t("wallet.readyToAttend"))
                const showTimer = setTimeout(() => setJustConnected(true), 0)
                const hideTimer = setTimeout(() => setJustConnected(false), 2000)
                return () => {
                    clearTimeout(showTimer)
                    clearTimeout(hideTimer)
                }
            }
        }
        prevKeyRef.current = key
    }, [publicKey, t])

    if (!publicKey) {
        return (
            <Button
                variant="outline"
                size="sm"
                onClick={() => setVisible(true)}
                disabled={connecting}
                aria-label={connecting ? t("common.loading") : t("wallet.connect")}
                className={cn(
                    "rounded-full border-primary/20 hover:bg-primary/5 text-primary transition-all",
                    isCollapsed ? "w-10 h-10 p-0 justify-center" : "gap-2"
                )}
            >
                {connecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Wallet className="w-4 h-4" />
                )}
                {!isCollapsed && <span className="hidden sm:inline">{t("wallet.connect")}</span>}
            </Button>
        )
    }

    // Format address: 4 chars ... 4 chars
    const formattedAddress = `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}`

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    aria-label={t("wallet.connectedWallet")}
                    className={cn(
                        "rounded-full border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary transition-all relative",
                        isCollapsed ? "w-10 h-10 p-0 justify-center" : "gap-2 font-mono",
                        justConnected && "ring-2 ring-green-500/50 border-green-500/30"
                    )}
                >
                    <Wallet className="w-4 h-4" />
                    {!isCollapsed && formattedAddress}
                    {/* Subtle network indicator dot on button */}
                    <NetworkIndicator className="absolute -top-0.5 -right-0.5 w-3 h-3 border-0 bg-transparent" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 max-w-[calc(100vw-2rem)] z-[9999] rounded-xl p-2">
                <div className="px-2 py-3 flex flex-col gap-1">
                    <span className="text-xs text-foreground-secondary uppercase tracking-wider font-semibold">
                        {t("wallet.connectedWallet")}
                    </span>
                    <span className="text-sm font-mono truncate">{publicKey.toBase58()}</span>
                </div>

                <div className="px-2 py-3 bg-card-secondary rounded-lg mb-2 flex items-center justify-between">
                    <span className="text-sm text-foreground-secondary">{t("wallet.balance")}</span>
                    <span className="font-medium text-sm">
                        {loadingBalance ? (
                            <Loader2 className="w-3 h-3 animate-spin inline" />
                        ) : balance !== null ? (
                            t("wallet.balanceSol", { balance: balance.toFixed(2) })
                        ) : (
                            "—"
                        )}
                    </span>
                </div>

                {/* Network Indicator - Always visible on devnet/testnet */}
                <div className="flex justify-center mb-2">
                    <NetworkBadge size="lg" showLabel />
                </div>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                    onClick={() => disconnect()}
                    className="cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive rounded-lg mt-1"
                >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("wallet.disconnect")}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
